import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Modal, RadioButton, SelectField, TextArea, TextInput } from 'is24-corecss';
import { FormattedMessage, useIntl } from 'react-intl';

import { jobTitleMapper, TeamMember, TeamMemberJobTitle, teamMemberJobTitles } from '../../../../models/TeamMember';
import { addTeamMember, performLifecycleTransition, updateTeamMember } from '../../../../api/TeamMemberApi';
import HttpError from '../../../../common/ExtendedError';
import { StatusMessageType } from '../../types/StatusMessageType';
import { getErrorMessage, getSuccessMessage, getWarningMessage } from '../../../../common/statusMesssages';
import { reportActionEvent, reportViewEvent } from '../../../../tracking/tracking';

export type TeamMemberFormValues = {
  email: string;
  username: string;
  salutation: string;
  firstName: string;
  lastName: string;
  note: string;
  sendInvitation: boolean;
  jobTitle: TeamMemberJobTitle | undefined;
};

type TeamMemberModalProps = {
  visible: boolean;
  onClose: () => void;
  selectedTeamMember: TeamMember | null;
  onUpdate: (status: StatusMessageType) => void;
  onCreate: (status: StatusMessageType) => void;
};

const defaultValues: TeamMemberFormValues = {
  email: '',
  username: '',
  salutation: '',
  firstName: '',
  lastName: '',
  note: '',
  sendInvitation: false,
  jobTitle: undefined,
};

const EMAIL_PATTERN = /^\s*\w+([-+.’]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*\s*$/i;

const TeamMemberModal: React.FC<TeamMemberModalProps> = ({ visible, onClose, onCreate, onUpdate, selectedTeamMember }: TeamMemberModalProps) => {
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors, isSubmitting },
    setError: setFieldError,
    reset,
  } = useForm<TeamMemberFormValues>({ defaultValues });

  const intl = useIntl();
  const EDIT_PAGE_VIEW_TITLE = 'edit_teammember';
  const ADD_PAGE_VIEW_TITLE = 'add_teammember';

  useEffect(() => {
    visible && reportViewEvent({ pageTitle: editMode ? EDIT_PAGE_VIEW_TITLE : ADD_PAGE_VIEW_TITLE });
    if (selectedTeamMember !== null) {
      reset({
        email: selectedTeamMember.email,
        username: selectedTeamMember.username || defaultValues.username,
        salutation: selectedTeamMember.salutation || defaultValues.salutation,
        firstName: selectedTeamMember.firstName || defaultValues.firstName,
        lastName: selectedTeamMember.lastName || defaultValues.lastName,
        note: selectedTeamMember.note || defaultValues.note,
        sendInvitation: false,
        jobTitle: selectedTeamMember.jobTitle || defaultValues.jobTitle,
      });
    } else {
      reset({});
    }
  }, [visible]);

  const onSubmitForm = () => async (values: TeamMemberFormValues) => {
    const teamMember: TeamMember = {
      salutation: values.salutation,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      note: values.note.trim() || null,
      jobTitle: values.jobTitle,
    };

    if (editMode) {
      await handleTeamMemberUpdate(teamMember);
    } else {
      await handleTeamMemberCreateAndInvite(teamMember, values);
    }
  };

  const handleTeamMemberCreateAndInvite = async (teamMember: TeamMember, values: TeamMemberFormValues) => {
    let isTeamMemberCreated = false;
    let createdTeamMember: TeamMember = { firstName: '' };
    try {
      teamMember.email = values.email.trim();
      createdTeamMember = await addTeamMember(teamMember);
      isTeamMemberCreated = true;
      if (values.sendInvitation) {
        await performLifecycleTransition(createdTeamMember.id!, { command: 'invite' });
      }
      onCreate(getSuccessMessage('teammanagement.statusMessage.teamMember.invitation.success'));
      reportActionEvent({ pageTitle: ADD_PAGE_VIEW_TITLE, label: 'teammember_added', event_name: 'teammanagement_member_added' });
      reset();
    } catch (e) {
      handleCreateAndInviteError(e, isTeamMemberCreated);
    }
  };

  const handleTeamMemberUpdate = async (teamMember: TeamMember) => {
    try {
      await updateTeamMember({ ...selectedTeamMember, ...teamMember });
      onUpdate(getSuccessMessage('teammanagement.statusMessage.teamMember.update.success'));
      reportActionEvent({ pageTitle: EDIT_PAGE_VIEW_TITLE, label: 'teammember_edited', event_name: 'teammanagement_member_edited' });
    } catch (e) {
      onUpdate(getErrorMessage(e));
    }
    reset();
  };

  const handleEmailDeniedError = (e: Error) => {
    if (e instanceof HttpError && e.body?.errorCode === 'teamMember.error.email.denied') {
      setFieldError(
        'email',
        {
          type: 'manual',
          message: intl.formatMessage({ id: 'teammanagement.statusMessage.error.message.teamMember.emailDenied' }),
        },
        { shouldFocus: true },
      );
      return true;
    }
    return false;
  };

  const handleCreateAndInviteError = (e: Error, isTeamMemberCreated: boolean) => {
    if (handleEmailDeniedError(e)) {
      return;
    } else if (isTeamMemberCreated) {
      onCreate(getWarningMessage('teammanagement.statusMessage.error.message.invitation.failed'));
    } else {
      onCreate(getErrorMessage(e));
    }
    reset();
  };

  const handleClose = (event: SyntheticEvent) => {
    if (event) {
      onClose();
      reset();
      setSalutationValid(true);
    }
  };

  const onClickInvitationButton = () => {
    setValue('sendInvitation', true);
    reportActionEvent({ pageTitle: ADD_PAGE_VIEW_TITLE, label: 'invitation_sent', event_name: 'teammanagement_invitation_sent' });
  };

  const jobTitleOptions = () => {
    const options: JSX.Element[] = [];
    teamMemberJobTitles.forEach((jobTitle) => {
      options.push(
        <option value={jobTitle} key={jobTitle}>
          {jobTitleMapper(jobTitle)}
        </option>,
      );
    });
    return <>{options}</>;
  };

  const editMode = selectedTeamMember !== null;
  const [salutationValid, setSalutationValid] = useState(true);

  return (
    <Modal visible={visible} onClose={handleClose} title={`Teammitglied ${editMode ? 'bearbeiten' : 'hinzufügen'}`} width={864}>
      <form onSubmit={handleSubmit(onSubmitForm())} data-testid='add-new-team-member'>
        <div className='grid grid-flex'>
          <div className='grid-item one-whole padding-bottom-s'>
            <TextInput
              data-testid='team-member-email'
              id='email'
              label={intl.formatMessage({
                id: editMode ? 'teammember.addModal.editMode.textInput.label.email' : 'teammember.addModal.textInput.label.email',
              })}
              placeholder='z.B. max.mustermann@xyz.com'
              errorMessage={errors.email?.message}
              valid={!errors.email}
              {...register('email', {
                required: intl.formatMessage({ id: 'teammember.addModal.textInput.email.validation.required' }),
                pattern: {
                  value: EMAIL_PATTERN,
                  message: intl.formatMessage({ id: 'teammember.addModal.textInput.email.validation.emailPattern' }),
                },
              })}
              disabled={editMode}
            />
          </div>
          <div className='status-info margin-bottom-l background-info'>
            <span className='margin-left-m margin-top-m status-message status-message--minimal status-info'>
              <b>Hinweis</b>
            </span>
            <p className='margin-left-m margin-bottom-m font-line-s margin-right-m'>
              Achten Sie auf die korrekte Schreibweise der E-Mail-Adresse, diese kann nachträglich nicht mehr angepasst werden. Bei falscher
              E-Mail-Adresse müssen Sie das Teammitglied löschen und neu anlegen.
            </p>
          </div>
          <ul className='grid-item one-whole padding-bottom-l'>
            <p className='font-bold'>{intl.formatMessage({ id: 'teammember.addModal.textInput.label.salutation' })}</p>
            <li className='radio-container grid-item one-fourth'>
              <RadioButton
                label='Herr'
                id='MR'
                value='MR'
                data-testid='MR'
                defaultChecked={selectedTeamMember?.salutation === 'MR'}
                isErroneous={!salutationValid}
                onClick={() => setSalutationValid(true)}
                {...register('salutation', {
                  validate: {
                    isSalutationValid: (value) => {
                      if (value === null) {
                        setSalutationValid(false);
                        return false;
                      } else {
                        return true;
                      }
                    },
                  },
                })}
              />
            </li>
            <li className='radio-container grid-item one-fourth'>
              <RadioButton
                label='Frau'
                id='MS'
                value='MS'
                data-testid='MS'
                defaultChecked={selectedTeamMember?.salutation === 'MS'}
                isErroneous={!salutationValid}
                onClick={() => setSalutationValid(true)}
                {...register('salutation')}
              />
            </li>
            <li className='radio-container grid-item one-fourth'>
              <RadioButton
                label='Divers'
                id='DIVERS'
                value='DIVERS'
                data-testid='DIVERS'
                defaultChecked={selectedTeamMember?.salutation === 'DIVERS'}
                isErroneous={!salutationValid}
                onClick={() => setSalutationValid(true)}
                {...register('salutation')}
              />
            </li>
            <li className='radio-container grid-item one-fourth'>
              <RadioButton
                label='Keine Angabe'
                id='NONE'
                value='NONE'
                data-testid='NONE'
                defaultChecked={selectedTeamMember?.salutation === 'NONE'}
                isErroneous={!salutationValid}
                onClick={() => setSalutationValid(true)}
                {...register('salutation')}
              />
            </li>
            <div className='font-error' hidden={salutationValid}>
              <FormattedMessage id='teammember.addModal.textInput.hint.message.salutation' />
            </div>
          </ul>
          <div className='grid-item one-whole padding-bottom-l'>
            <TextInput
              id='firstName'
              label={intl.formatMessage({ id: 'teammember.addModal.textInput.label.firstName' })}
              placeholder='z.B. Max'
              errorMessage={errors.firstName?.message || errors.firstName?.type}
              valid={!errors.firstName}
              helperText=''
              {...register('firstName', {
                required: intl.formatMessage({ id: 'teammember.addModal.textInput.firstName.validation.required' }),
                validate: {
                  notEmpty: (value) =>
                    value.trim() === '' ? intl.formatMessage({ id: '"teammember.addModal.textInput.firstName.validation.notEmpty' }) : true,
                },
              })}
            />
          </div>
          <div className='grid-item one-whole padding-bottom-l'>
            <TextInput
              id='lastName'
              label={intl.formatMessage({ id: 'teammember.addModal.textInput.label.lastName' })}
              placeholder='z.B. Mustermann'
              errorMessage={errors.lastName?.message || errors.lastName?.type}
              valid={!errors.lastName}
              helperText=''
              {...register('lastName', {
                required: intl.formatMessage({ id: 'teammember.addModal.textInput.lastName.validation.required' }),
                validate: {
                  notEmpty: (value) =>
                    value.trim() === '' ? intl.formatMessage({ id: '"teammember.addModal.textInput.lastName.validation.notEmpty' }) : true,
                },
              })}
            />
          </div>
          <div className='grid-item one-whole padding-bottom-l'>
            <SelectField
              id='jobTitle'
              label={intl.formatMessage({ id: 'teammember.addModal.textInput.label.jobTitle' })}
              errorMessage={errors.jobTitle?.message || errors.jobTitle?.type}
              {...register('jobTitle')}
            >
              {jobTitleOptions()}
            </SelectField>
          </div>
          <div className='grid-item one-whole padding-bottom-l'>
            <TextArea
              id='note'
              label={intl.formatMessage({ id: 'teammember.addModal.textInput.label.note' })}
              placeholder='Zusätzliche Notizen zum Teammitglied. Nur für Sie sichtbar.'
              {...register('note')}
              maxLength={50}
            />
          </div>

          {editMode && (
            <>
              <div className='grid-item one-half padding-right-m'>
                <button className='link-text' data-testid='cancel-button' onClick={handleClose}>
                  Abbrechen
                </button>
              </div>
              <div className='grid-item one-half padding-left-m'>
                <Button
                  data-testid='update-team-member-button'
                  disabled={isSubmitting}
                  appearance='primary'
                  className='one-whole'
                  onClick={handleSubmit(onSubmitForm())}
                >
                  <span className='is24-icon-positive-check margin-right-s font-bold' />
                  <FormattedMessage id='teammember.addModal.editMode.button.save' />
                </Button>
              </div>
            </>
          )}
          {!editMode && (
            <>
              <div className='grid-item one-half padding-top-s'>
                <button className='link-text' onClick={handleClose}>
                  Abbrechen
                </button>
              </div>
              <div className='grid-item one-half  padding-left-m'>
                <Button
                  data-testid='create-team-member-invite-button'
                  appearance='primary'
                  className='one-whole'
                  disabled={isSubmitting}
                  onClick={() => {
                    handleSubmit(onSubmitForm());
                    onClickInvitationButton();
                  }}
                >
                  <span className='is24-icon-paperplane margin-right-s' />
                  <FormattedMessage id='teammember.addModal.createMode.button.createAndInvite' />
                </Button>
              </div>
            </>
          )}
        </div>
        <div className='font-line-s padding-top-m'>Mit * gekennzeichnete Felder müssen ausgefüllt werden.</div>
      </form>
    </Modal>
  );
};

export default TeamMemberModal;
